import { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from 'reactstrap';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { initialValues, validationSchema } from './constants';
import './EditRole.scss';
import { fetchPrivileges, saveRole } from '../../../../store/apis/adminService';

function EditRole({ roleDetails, isOpen, toggle, handleRoleUpdated }) {
  const [formKey, setFormKey] = useState(0);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      ...initialValues,
    },
    resolver: yupResolver(validationSchema),
  });

  // Fetch privileges from API
  const { data: privileges, isLoading: isLoadingPrivileges } = useQuery({
    queryKey: ['privileges'],
    queryFn: fetchPrivileges,
  });

  // Setup field array for privilegeIds
  const { fields } = useFieldArray({
    control,
    name: 'privilegeIds',
  });

  const isBootstrapping =
    isLoadingPrivileges ||
    !roleDetails ||
    !Array.isArray(privileges) ||
    privileges.length === 0;

  useEffect(() => {
    if (!Array.isArray(privileges)) return;

    // If roleDetails isn't ready yet, still prebuild the array (all false)
    const picked = new Set(
      roleDetails?.privileges?.map(x => x?.PrivilegeID) ?? [],
    );

    const mappedPrivileges = privileges.map(p => ({
      privilegeId: p.ID,
      status: picked.has(p.ID),
    }));

    // Always reset when privileges change (and/or roleDetails updated)
    if (privileges.length) {
      setFormKey(prev => prev + 1);
      reset(
        {
          id: roleDetails?.role?.ID ?? 0,
          roleName: roleDetails?.role?.Name ?? '',
          isActive: roleDetails?.role?.IsActive ? 'true' : 'false',
          privilegeIds: mappedPrivileges,
        },
        { keepDefaultValues: false },
      );
    }
  }, [privileges, roleDetails, reset]);

  const onCancel = async () => {
    toggle();
    reset(initialValues);
    await trigger();
  };

  // Mutation for creating a role
  const { mutate: editRoleMutate, isPending: isUpdating } = useMutation({
    mutationFn: saveRole,
    onSuccess: () => {
      toast.success('Role Updated Successfully!', {
        className: 'toast-success',
      });
      reset(initialValues); // Reset form after submission
      handleRoleUpdated();
      onCancel();
    },
    onError: err => {
      toast.error(err?.message || 'Failed to create role', {
        className: 'toast-error',
      });
    },
  });

  const onSubmit = values => {
    const payload = {
      id: values.id,
      roleName: values.roleName,
      isActive: values.isActive === 'true',
      privilegeIds: values.privilegeIds
        .filter(p => p.status)
        .map(p => p.privilegeId),
    };
    editRoleMutate(payload);
  };

  // Render privilege body with validation
  const renderPrivilegeBody = () => {
    if (isBootstrapping) {
      return (
        <tr>
          <td colSpan={2} className="text-center">
            <Spinner size="sm" color="primary" /> Loading...
          </td>
        </tr>
      );
    }

    // Now it's safe to check fields (they've been reset with mapped data)
    if (fields.length === 0) {
      return (
        <tr>
          <td colSpan={2} className="text-center">
            No Privileges Found
          </td>
        </tr>
      );
    }

    return fields.map((item, index) => (
      <tr key={item.id}>
        <td>{privileges?.find(p => p.ID === item.privilegeId)?.Name || ''}</td>
        <td>
          <Controller
            control={control}
            name={`privilegeIds.${index}.status`}
            rules={{ required: true }}
            render={({ field }) => (
              <FormGroup switch>
                <Input
                  id={`switch-${index}`}
                  type="switch"
                  {...field}
                  checked={!!field.value}
                  className={`form-check-input ${errors?.privilegeIds?.[index]?.status ? 'is-invalid' : ''}`}
                />
                <label className="ms-2 fw-light" htmlFor={`switch-${index}`}>
                  {field.value ? 'Yes' : 'No'}
                </label>
                {errors?.privilegeIds?.[index]?.status && (
                  <div className="invalid-feedback d-block">Required</div>
                )}
              </FormGroup>
            )}
          />
        </td>
      </tr>
    ));
  };

  return (
    <Offcanvas
      className="edit-role-oc"
      isOpen={isOpen}
      toggle={onCancel}
      direction="end"
    >
      <OffcanvasHeader className="edit-role-oc-header">
        Edit Role
      </OffcanvasHeader>
      <Form
        key={formKey}
        onSubmit={handleSubmit(onSubmit)}
        className="edit-role-form"
      >
        <OffcanvasBody className="edit-role-oc-body">
          {isBootstrapping ? (
            <div className="d-flex align-items-center justify-content-center gap-2 w-100">
              <Spinner size="sm" color="primary" /> Loading...
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {/* Role Name */}
              <div>
                <Label>
                  Role Name <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="roleName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="text"
                      className={`form-control external-filter-input ${errors?.roleName && 'is-invalid'}`}
                      placeholder="Enter Role Name"
                      {...field}
                    />
                  )}
                />
                {errors?.roleName && (
                  <div className="invalid-feedback">
                    {errors?.roleName?.message}
                  </div>
                )}
              </div>
              {/* Role Status */}
              <div>
                <Label>
                  Status <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="select"
                      className={`form-select external-filter-input ${errors?.isActive && 'is-invalid'}`}
                      {...field}
                    >
                      <option value="">Select Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </Input>
                  )}
                />
                {errors?.isActive && (
                  <div className="invalid-feedback">
                    {errors?.roleName?.message}
                  </div>
                )}
              </div>
              {/* Privileges */}
              <div className="privileges-section">
                <Label>
                  Privileges <span className="text-danger">*</span>
                </Label>
                <div className="table-responsive privileges">
                  <Table
                    hover
                    className="custom-table align-middle text-nowrap"
                  >
                    <thead>
                      <tr>
                        <th className="fw-semibold">Privileges</th>
                        <th className="fw-semibold">Access</th>
                      </tr>
                    </thead>
                    <tbody>{renderPrivilegeBody()}</tbody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </OffcanvasBody>
        <div className="edit-role-oc-footer d-flex flex-row align-items-center gap-2 w-100 p-3">
          <Col>
            <button
              type="button"
              className="edit-role-cancel w-100"
              onClick={onCancel}
            >
              Cancel
            </button>
          </Col>
          <Col>
            <button
              type="submit"
              className="edit-role-add w-100"
              disabled={!isValid || isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update'}
            </button>
          </Col>
        </div>
      </Form>
    </Offcanvas>
  );
}

export default EditRole;
